<template>
  <div
    class="relative flex items-center justify-center h-full mx-auto transition-all duration-500 onboarding-step !bg-transparent">
    <!-- Step transition -->
    <Transition name="fade-slide" mode="out-in">
      <div
        :key="currentStep"
        class="z-10 p-4 w-full text-white rounded-lg lg:p-24">
        <div class="mb-2">
          <h3 class="mb-2 text-lg font-semibold lg:text-3xl text-balance">
            {{ steps[currentStep].title }}
          </h3>
          <p class="mb-1 text-gray-100/90">
            {{ steps[currentStep].description }}
          </p>
          <p
            v-if="steps[currentStep].submessage"
            class="mb-1 text-sm text-gray-100/90">
            {{ steps[currentStep].submessage }}
          </p>
        </div>

        <div v-if="steps[currentStep].key === 'username'" class="space-y-4">
          <UsernameSection :profile="profile" />
          <DisplayNameSection :profile="profile" />
        </div>

        <div
          v-if="steps[currentStep].key === 'avatar'"
          class="space-y-2 divide-y divide-gray-300">
          <AvatarSection ref="avatarSection" class="py-2" :profile="profile" auto-edit />
        </div>

        <div
          v-if="steps[currentStep].key === 'follow'"
          class="space-y-2 divide-y divide-gray-300">
          <FollowSuggestionsSection class="py-2" :profile="profile" />
        </div>

        <!-- Form step -->
        <form
          v-if="steps[currentStep].type === 'form'"
          @submit.prevent="handleSubmit">
          <div
            v-for="(field, index) in steps[currentStep].fields"
            :key="index"
            class="mb-4">
            <label
              :for="field.name"
              class="block text-sm font-medium leading-6 text-gray-100/70">
              {{ field.label }}
            </label>
            <div class="mt-2">
              <input
                :id="field.name"
                v-model="form[field.name]"
                :type="field.type"
                :placeholder="field.placeholder"
                class="block py-1.5 w-full text-gray-900 rounded-md border-0 ring-1 ring-inset ring-gray-300 shadow-xs placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6" />
            </div>
          </div>
          <button
            type="submit"
            class="flex gap-x-2 justify-center items-center py-3 px-4 w-full text-sm font-semibold text-center text-white to-indigo-500 rounded-lg border border-transparent transition ease-in-out from-sky-500 bg-linear-to-r hover:from-sky-400 hover:to-indigo-400 disabled:opacity-50 disabled:pointer-events-none"
            :disabled="formBusy">
            <span v-if="!formBusy">Save</span>
            <span v-if="formBusy" class="ml-2">
              <fa-icon icon="spinner-third" spin />
            </span>
          </button>
        </form>

        <!-- Static content step -->
        <div v-else-if="steps[currentStep].type === 'static'" class="text-left">
          <p>{{ steps[currentStep].content }}</p>
        </div>

        <!-- Navigation buttons -->
        <div class="flex justify-between mt-4">
          <button
            v-if="currentStep > 0 && currentStep < steps.length - 1"
            class="inline-flex gap-x-2 justify-between items-center py-2 px-4 text-sm font-semibold text-center text-white to-indigo-500 rounded-lg transition ease-in-out cursor-pointer from-sky-500 bg-linear-to-r hover:from-sky-400 hover:to-indigo-400 disabled:opacity-50 disabled:pointer-events-none"
            @click="previousStep">
            <fa-icon icon="arrow-left" />
            Back
          </button>
          <span v-else></span>
          <button
            v-if="currentStep < steps.length - 1"
            class="inline-flex gap-x-2 justify-between items-center py-2 px-4 text-sm font-semibold text-center text-white to-indigo-500 rounded-lg transition ease-in-out cursor-pointer from-sky-500 bg-linear-to-r hover:from-sky-400 hover:to-indigo-400 disabled:opacity-50 disabled:pointer-events-none"
            @click="nextStep">
            Next
            <fa-icon icon="arrow-right" />
          </button>
          <button
            v-if="currentStep === steps.length - 1"
            class="flex gap-x-2 justify-between items-center py-2 px-4 w-full text-sm font-semibold text-center text-white to-indigo-500 rounded-lg transition ease-in-out cursor-pointer from-sky-500 bg-linear-to-r hover:from-sky-400 hover:to-indigo-400 disabled:opacity-50 disabled:pointer-events-none"
            @click.prevent="completeWelcome">
            Let's Go
            <fa-icon icon="rocket" class="group-hover:animate-bounce" />
          </button>
        </div>
      </div>
    </Transition>
  </div>
</template>
<script setup>
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useStoreProfile } from '@/stores/storeProfile'
import { useStoreAuth } from '@/stores/storeAuth'
import UsernameSection from '@/components/UserSettings/UsernameSection'
import AvatarSection from '@/components/UserSettings/AvatarSection'
import DisplayNameSection from '@/components/UserSettings/DisplayNameSection'
import FollowSuggestionsSection from '@/components/UserSettings/FollowSuggestionsSection'

// Store dependencies
const storeProfile = useStoreProfile()
const storeAuth = useStoreAuth()
const router = useRouter()

const profile = computed(() => {
  return storeProfile.getCurrentUserProfile
})

// Reactive Variables
const formBusy = ref(false)
const currentStep = ref(0)
const avatarSection = ref(null)
const form = ref({
  username: '',
  email: '',
  phone: '',
  bio: ''
})

// Steps Configuration
const steps = ref([
  {
    key: 'username',
    title: 'Pick a Username'
    // description: 'Choose a unique username for your account.'
    // submessage: "We've pre-filled a suggestion for you."
  },

  {
    key: 'avatar',
    title: 'Set an avatar'
    // description: 'You can change this later from your profile'
    // submessage: "We've pre-filled a suggestion for you."
  },

  {
    key: 'follow',
    title: 'Add to your circle'
    // description: 'Follow friends and family to see their reviews'
    // submessage: "We've pre-filled a suggestion for you."
  },
  // {
  //   title: 'Add Contact Info',
  //   description: 'Add your email or phone number (optional).',
  //   type: 'form',
  //   fields: [
  //     {
  //       name: 'email',
  //       label: 'Email',
  //       type: 'email',
  //       placeholder: 'Enter your email'
  //     },
  //     {
  //       name: 'phone',
  //       label: 'Phone',
  //       type: 'tel',
  //       placeholder: 'Enter your phone number'
  //     }
  //   ]
  // },
  // {
  //   title: 'What did you watch last?',
  //   description: 'Search for a title to get started.',
  //   type: 'form',
  //   fields: [
  //     {
  //       name: 'search',
  //       label: 'Search Spectra',
  //       type: 'text',
  //       placeholder: 'Search for a title'
  //     }
  //   ]
  // },
  // {
  //   title: 'Find others to follow',
  //   description: '',
  //   type: 'form',
  //   fields: [
  //     {
  //       name: 'search',
  //       label: 'Search Spectra Users',
  //       type: 'text',
  //       placeholder: 'Search for others'
  //     }
  //   ]
  // },
  {
    title: 'All Done!',
    // description: 'Your profile is ready to go.',
    type: 'static'
  }
])

// Navigation Logic
const nextStep = async () => {
  // Auto-save avatar when leaving the avatar step
  if (
    steps.value[currentStep.value].key === 'avatar' &&
    avatarSection.value?.hasChanges()
  ) {
    try {
      await avatarSection.value.saveForOnboarding()
    } catch (error) {
      console.error('Error saving avatar during onboarding:', error)
    }
  }

  if (currentStep.value < steps.value.length - 1) {
    currentStep.value++
  }
}

const previousStep = () => {
  if (currentStep.value > 0) {
    currentStep.value--
  }
}

// Form Submission Handler
const handleSubmit = async () => {
  formBusy.value = true
  try {
    console.log('Form submitted:', form.value)
    nextStep()
  } catch (error) {
    console.error('Error submitting form:', error)
  } finally {
    formBusy.value = false
  }
}

const completeWelcome = () => {
  storeProfile.setOnboardingComplete()
  router.push({ name: 'WebIndex' })
}
</script>

<style>
/* Fade transition */
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: opacity 0.3s ease;
}

.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
}

.fade-slide-enter-to,
.fade-slide-leave-from {
  opacity: 1;
}
</style>
